import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { stripePromise } from "../../../stripe";
import logoSm from "../../../assets/images/logo-sm.png";
import axios from "../../../axios-instance";
import clsx from "clsx";
import Loader from "../../../components/Loader/Loader";
import { getAdminBusinessId, getUserBusinesses } from "../../../utils/authHelper";
import { getPlanDisplayPrice } from "../../../utils/planLimits";
import { confirmStripeCheckout, getCheckoutCard } from "../../../utils/confirmStripeCheckout";
import { isTrialEligible, TRIAL_DAYS } from "../../../utils/trialAccess";
import { fetchUserFeatures } from "../../../actions/featureAccessActions";
import store from "../../../store";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#0C4767",
      fontFamily: "Sofia Pro, Inter, Arial, sans-serif",
      "::placeholder": {
        color: "#9FB3C8",
      },
    },
    invalid: {
      color: "#e53e3e",
    },
  },
};

const TrialNotice = () => (
  <div className="checkout-notice">
    <span className="checkout-notice__icon" aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M8 2.2l1.05 3.35h3.45l-2.8 2.08 1.07 3.37L8 8.95 5.23 11l1.07-3.37-2.8-2.08h3.45L8 2.2z"
          fill="#1B9A62"
        />
      </svg>
    </span>
    <p>
      <strong>You won’t be charged today.</strong> We’ll charge your card after
      14 days unless you cancel. SMS reminders unlock after you subscribe.
    </p>
  </div>
);

const PLAN_COPY = {
  free: {
    tagline: "Start automating your basic scheduling.",
    summary: "Basic scheduling, free forever",
    features: [
      "Basic scheduling features",
      "Create your first booking page",
      "No payment details needed",
    ],
  },
  standard: {
    tagline: "For individuals who need more control.",
    summary: "Advanced scheduling for individuals",
    features: [
      "Everything in Free",
      "Advanced scheduling features",
      "1 round robin event type",
    ],
  },
  teams: {
    tagline: "For teams coordinating bookings together.",
    summary: "Team scheduling and shared events",
    features: [
      "Everything in Standard",
      "Team scheduling",
      "Shared event types",
    ],
  },
};

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.5 8.2l3 3 6-6.4"
      stroke="#45C48A"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SelectedBadge = () => (
  <span className="plan-card__selected" aria-hidden="true">
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="9" fill="#0C4767" />
      <path
        d="M5 9.1l2.6 2.5 5.4-5.6"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const formatMoney = (amount) => {
  const n = Number(amount) || 0;
  return n % 1 === 0 ? `$${n}` : `$${n.toFixed(2)}`;
};

const planKey = (plan) => {
  const name = String(plan?.plan_name || "").toLowerCase();
  if (Number(plan?.monthly_price) === 0 || name.includes("free")) return "free";
  if (name.includes("team")) return "teams";
  if (name.includes("standard")) return "standard";
  return "standard";
};

const getPlanCopy = (plan) => PLAN_COPY[planKey(plan)] || PLAN_COPY.standard;

const resolveBusinessId = () => {
  const stored = getAdminBusinessId();
  if (stored) return stored;
  const businesses = getUserBusinesses();
  const ids = businesses ? Object.values(businesses) : [];
  if (ids.length > 0) {
    const id = ids[ids.length - 1];
    localStorage.setItem("businessId", id);
    return id;
  }
  return null;
};

const PaymentForm = () => {
  const [pageLoading, setPageLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(null);
  const [planId, setPlanId] = useState("");
  const [planType, setPlanType] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [cardError, setCardError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [trialEligible, setTrialEligible] = useState(true);

  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const processingRef = useRef(false);

  useEffect(() => {
    setPageLoading(true);
    Promise.all([
      axios.get("/admin/plans").then((response) => {
        const list = [...(response.data || [])].sort(
          (a, b) => Number(a.monthly_price) - Number(b.monthly_price)
        );
        setPlans(list);
        const free = list.find((p) => Number(p.monthly_price) === 0);
        setPlanId(String(free?.id ?? list[0]?.id ?? ""));
      }),
      axios
        .get("user-details")
        .then((res) => {
          const details = res?.data?.data ?? res?.data;
          setTrialEligible(isTrialEligible(details));
        })
        .catch(() => {
          // Keep default eligibility. 401 is handled by the axios interceptor.
        }),
    ])
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, []);

  const selectedPlan = plans.find((p) => String(p.id) === String(planId));
  const isFreePlan = Boolean(selectedPlan && +selectedPlan.monthly_price === 0);
  const showBilling = Boolean(selectedPlan) && !isFreePlan;
  const selectedCopy = selectedPlan ? getPlanCopy(selectedPlan) : null;
  const isAnnual = planType === "annual";
  const selectedDisplayPrice = selectedPlan
    ? isFreePlan
      ? 0
      : getPlanDisplayPrice(selectedPlan, planType)
    : 0;

  const baseAmount = (() => {
    if (!selectedPlan) return 0;
    return Number(
      planType === "monthly"
        ? selectedPlan.monthly_price
        : selectedPlan.annual_price
    );
  })();

  const discount = appliedCoupon
    ? appliedCoupon.type === "P"
      ? parseFloat(((baseAmount * appliedCoupon.value) / 100).toFixed(2))
      : Math.min(parseFloat(appliedCoupon.value), baseAmount)
    : 0;

  const totalAmount = Math.max(0, parseFloat((baseAmount - discount).toFixed(2)));
  const dueToday = trialEligible && !isFreePlan ? 0 : totalAmount;

  const nextPaymentDate = (() => {
    const d = new Date();
    if (trialEligible && !isFreePlan) {
      d.setDate(d.getDate() + TRIAL_DAYS);
    } else if (planType === "annual") {
      d.setFullYear(d.getFullYear() + 1);
    } else {
      d.setDate(d.getDate() + 30);
    }
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  })();

  const handleApplyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) {
      setCouponError("Please enter a coupon code.");
      return;
    }
    setCouponLoading(true);
    setCouponError("");
    setAppliedCoupon(null);
    try {
      const res = await axios.get(
        `/admin/subscription/validate-coupon/${encodeURIComponent(code)}`
      );
      if (res.data?.success) {
        setAppliedCoupon(res.data.coupon);
        setCouponError("");
      } else {
        setCouponError(res.data?.message || "Invalid or expired coupon code.");
      }
    } catch (err) {
      setCouponError(
        err.response?.data?.message ||
          "Could not validate coupon. Please try again."
      );
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponError("");
  };

  const handleFreePlanSubmit = () => {
    navigate("/signup/thanksForPayment?plan=free");
  };

  const handlePaidSubmit = async (e) => {
    e.preventDefault();
    if (processingRef.current) return;
    setCardError(null);

    if (!planId || !selectedPlan) {
      setCardError("Please select a plan.");
      return;
    }

    if (isFreePlan) {
      handleFreePlanSubmit();
      return;
    }

    if (!stripe || !elements) {
      setCardError("Payment form is still loading. Please try again.");
      return;
    }

    const card = getCheckoutCard(elements);
    if (!card) {
      setCardError("Please enter your card details.");
      return;
    }

    const businessId = resolveBusinessId();
    if (!businessId) {
      setCardError("Your workspace could not be found. Please refresh and try again.");
      return;
    }

    setProcessing(true);
    processingRef.current = true;

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card,
      });

      if (error) {
        setCardError(error.message);
        setProcessing(false);
        processingRef.current = false;
        return;
      }

      const payload = {
        payment_method_id: paymentMethod.id,
        plan_id: selectedPlan.id,
        plan_type: planType,
        business_id: businessId,
      };
      if (appliedCoupon?.code) {
        payload.coupon_code = appliedCoupon.code;
      }

      const res = await axios.post("/admin/subscription/create", payload);

      if (res.data.success) {
        const { error: confirmError } = await confirmStripeCheckout({
          stripe,
          elements,
          clientSecret: res.data.client_secret,
          setupClientSecret: res.data.setup_client_secret,
        });

        if (confirmError) {
          setCardError(confirmError.message);
          setProcessing(false);
          processingRef.current = false;
          return;
        }

        const startedTrial = Boolean(res.data.is_trial);
        setLoadingText(
          startedTrial
            ? "Starting your 14-day trial…"
            : "Activating your subscription…"
        );
        setPageLoading(true);
        try {
          await axios.post("/admin/subscription/confirm", {
            subscription_id: res.data.subscription_id,
            business_id: businessId,
          });
          store.dispatch(fetchUserFeatures());
        } catch (confirmErr) {
          console.error("Subscription confirm error", confirmErr);
        }
        setPageLoading(false);
        setProcessing(false);
        navigate(
          startedTrial
            ? "/signup/thanksForPayment?trial=1"
            : "/signup/thanksForPayment"
        );
        return;
      }

      setCardError(res.data.message || "Error creating subscription.");
      setProcessing(false);
      processingRef.current = false;
    } catch (err) {
      console.error(err);
      setCardError(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
      setProcessing(false);
      processingRef.current = false;
    }
  };

  const ctaLabel = processing
    ? "Processing..."
    : isFreePlan
    ? "Continue with Free"
    : trialEligible
    ? "Start 14-day free trial"
    : "Subscribe now";

  return (
    <>
      <Loader isShowLoader={pageLoading} loadingText={loadingText} />

      <div className="checkout-page">
        <header className="checkout-header">
          <div className="header-container">
            <div className="logo">
              <img src={logoSm} alt="MeetOcto" />
            </div>
          </div>
        </header>

        <div className="checkout-section">
          <form onSubmit={handlePaidSubmit} className="checkout">
            <div className="checkout-intro">
              <h1>Choose the plan that fits your scheduling needs</h1>
              <p>
                Start free, or try a paid plan for {TRIAL_DAYS} days. You can
                change or cancel at any time.
              </p>
            </div>

            <div className="checkout-billing">
              <div className="checkout-billing__toggle" role="group" aria-label="Billing period">
                <button
                  type="button"
                  className={clsx("checkout-billing__btn", {
                    "is-active": planType === "monthly",
                  })}
                  onClick={() => setPlanType("monthly")}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={clsx("checkout-billing__btn", {
                    "is-active": planType === "annual",
                  })}
                  onClick={() => setPlanType("annual")}
                >
                  Annually
                </button>
              </div>
              {planType === "monthly" && (
                <p className="checkout-billing__save">Save 20% with annual billing</p>
              )}
            </div>

            <div className="checkout-layout">
              <div className="checkout-main">
              <div className={`checkout-plans checkout-plans--${plans.length || 3}`}>
                {plans.map((plan) => {
                  const selected = planId === String(plan.id);
                  const copy = getPlanCopy(plan);
                  const isFree = Number(plan.monthly_price) === 0;
                  const displayPrice = isFree
                    ? 0
                    : getPlanDisplayPrice(plan, planType);

                  return (
                    <button
                      type="button"
                      key={plan.id}
                      className={clsx("plan-card", { "is-selected": selected })}
                      onClick={() => {
                        setPlanId(String(plan.id));
                        setCardError(null);
                      }}
                    >
                      {selected && <SelectedBadge />}
                      <div className="plan-card__top">
                        <h2>{plan.plan_name}</h2>
                        <p className="plan-card__tagline">{copy.tagline}</p>
                        <div className="plan-card__price">
                          <span className="plan-card__amount">
                            {formatMoney(displayPrice)}
                          </span>
                          <span className="plan-card__period">
                            {isFree ? "/ forever" : "/ user / month"}
                          </span>
                        </div>
                        {!isFree && trialEligible && (
                          <p className="plan-card__trial">14-day free trial</p>
                        )}
                        {isAnnual && !isFree && (
                          <p className="plan-card__billed">
                            Billed annually ({formatMoney(plan.annual_price)}/yr)
                          </p>
                        )}
                      </div>
                      <ul className="plan-card__features">
                        {copy.features.map((feature) => (
                          <li key={feature}>
                            <CheckIcon />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </button>
                  );
                })}
              </div>

              {showBilling && (
                <section className="checkout-details" aria-label="Billing details">
                  <h2>Billing details</h2>
                  {trialEligible ? (
                    <p className="checkout-details__copy">
                      <strong>You won’t be charged today.</strong> We’ll charge
                      your card after 14 days unless you cancel. SMS reminders
                      unlock after you subscribe.
                    </p>
                  ) : (
                    <p className="checkout-details__copy">
                      Enter your card to start your {selectedPlan.plan_name}{" "}
                      subscription.
                    </p>
                  )}

                  <div className="checkout-details__fields">
                    <label className="checkout-field checkout-field--number">
                      <span>Card number</span>
                      <div className="checkout-field__input">
                        <CardNumberElement options={CARD_ELEMENT_OPTIONS} />
                      </div>
                    </label>
                    <label className="checkout-field checkout-field--expiry">
                      <span>Expiry date</span>
                      <div className="checkout-field__input">
                        <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
                      </div>
                    </label>
                    <label className="checkout-field checkout-field--cvc">
                      <span>CVC</span>
                      <div className="checkout-field__input">
                        <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
                      </div>
                    </label>
                  </div>
                  {cardError && <p className="checkout-error">{cardError}</p>}
                </section>
              )}
              </div>

              <aside className="checkout-summary">
                <h2>Order summary</h2>

                {selectedPlan ? (
                  <div className="checkout-summary__plan">
                    <div>
                      <p className="checkout-summary__name">
                        {selectedPlan.plan_name} plan
                      </p>
                      <p className="checkout-summary__desc">
                        {isFreePlan
                          ? selectedCopy.summary
                          : trialEligible
                          ? `14-day free trial · then ${formatMoney(
                              selectedDisplayPrice
                            )} / user / month`
                          : `${isAnnual ? "Annual" : "Monthly"} billing`}
                      </p>
                    </div>
                    <span className="checkout-summary__price">
                      {formatMoney(selectedDisplayPrice)}
                    </span>
                  </div>
                ) : (
                  <p className="checkout-summary__empty">Select a plan to continue.</p>
                )}

                {showBilling && trialEligible && <TrialNotice />}

                <div className="checkout-coupon">
                  <label htmlFor="couponCode">Have a coupon code?</label>
                  {appliedCoupon ? (
                    <div className="checkout-coupon__applied">
                      <span>
                        {appliedCoupon.code} applied
                        {appliedCoupon.type === "P"
                          ? ` (−${appliedCoupon.value}%)`
                          : ` (−$${appliedCoupon.value})`}
                      </span>
                      <button type="button" onClick={handleRemoveCoupon}>
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="checkout-coupon__row">
                      <input
                        type="text"
                        name="couponCode"
                        id="couponCode"
                        value={couponInput}
                        onChange={(e) => {
                          setCouponInput(e.target.value);
                          if (couponError) setCouponError("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleApplyCoupon();
                          }
                        }}
                        placeholder="Enter coupon code"
                        disabled={couponLoading}
                      />
                      <button
                        className="checkout-apply"
                        type="button"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading || !couponInput.trim()}
                      >
                        {couponLoading ? "…" : "Apply"}
                      </button>
                    </div>
                  )}
                  {couponError && <p className="checkout-error">{couponError}</p>}
                </div>

                {cardError && !showBilling && (
                  <p className="checkout-error">{cardError}</p>
                )}

                <div className="checkout-total">
                  <div>
                    <p className="checkout-total__label">Total due today</p>
                    <p className="checkout-total__hint">
                      {isFreePlan
                        ? "No payment required"
                        : trialEligible
                        ? "No charge today"
                        : `Next payment ${nextPaymentDate}`}
                    </p>
                    {discount > 0 && (
                      <p className="checkout-total__discount">
                        {trialEligible && !isFreePlan
                          ? `Coupon saved for your first charge (−${formatMoney(
                              discount
                            )})`
                          : `Discount −${formatMoney(discount)}`}
                      </p>
                    )}
                  </div>
                  <span
                    className={clsx("checkout-total__amount", {
                      "is-free": dueToday === 0,
                    })}
                  >
                    {formatMoney(dueToday)}
                  </span>
                </div>

                <button
                  className="checkout-cta"
                  type="submit"
                  disabled={processing || !planId}
                >
                  {ctaLabel}
                </button>

                <p className="checkout-help">
                  Questions?{" "}
                  <a href="mailto:info@meetocto.com">Contact our team</a>
                </p>
              </aside>
            </div>
          </form>
        </div>

        <footer>
          <div className="footer-container">
            <ul className="languages d-none">
              <li>English (UK)</li>
            </ul>
            <p>© {new Date().getFullYear()} MeetOcto. A JB Media LLC company. All Rights Reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

const Payment = () => {
  return (
    <Elements
      stripe={stripePromise}
      options={{ appearance: { theme: "stripe" } }}
    >
      <PaymentForm />
    </Elements>
  );
};

export default Payment;
